import { sleep } from "@do/next-utils";
import type {
  BlockText,
  ChatbotParams,
  ChatCustomBlock,
  ChatFastAction,
  ChatMessage,
  Locale,
} from "@do/shared-types";
import { ChatCustomBlockTypes, DoChatAction } from "@do/shared-types";
import type { ChatContainerUiProps, EditBotResponseProps } from "@do/ui-chat";
import { ChatContainerUi } from "@do/ui-chat";
import { Box, CircularProgress } from "@mui/material";
import type { GetServerSideProps, GetServerSidePropsContext } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getGenericErrorMessage,
  getMessageForError,
  getOutOfCreditsErrorMessage,
} from "../src/errors";
import { trpc } from "../src/gateways/trpc";
import { appendHttpsSchemeIfNecessary, getQueryParams, localeSwitcher } from "../src/helpers";
import { useTheming } from "../src/hooks";
import {
  sendQuestionToDoBot,
  sendQuestionToDoChat,
  StreamedMessagesReceiver,
} from "../src/SenderReceiver";
import type { DoBotResponse, ForDoBotPayload, FromDoBotBlock } from "../src/types/common";
import { staticRootStyles } from "../src/ui/styles";

const FAKE_STREAMING_NB_STREAMED_LETTERS_AT_ONCE = 3;
const FAKE_STREAMING_DELAY_MS = 8;
const TIMEOUT_AFTER_GREETING_BEFORE_DISABLING_SSE_MS = 3000;

export type ChatMessageWithChangeTracker = ChatMessage & {
  streamingChangesCounter?: number;
};

export const DochatConfigPage = () => {
  const [chatbotParams, setChatbotParams] = useState<ChatbotParams | null>(null);
  const [currentLocale, setCurrentLocale] = useState<Locale | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [isStreamingDisabled, setIsStreamingDisabled] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [mergedCtxData, setMergedCtxData] = useState<Record<string, string> | null>(null);
  const [isOutOfCredits, setIsOutOfCredits] = useState(false);
  const didReceiveResponseToInitialGreeting = useRef<boolean>(false);
  const chatInputRef = useRef<HTMLInputElement>(null);
  const dotsLoading = useRef<ChatMessage>({
    id: "dotsLoading",
    blocks: [{ type: ChatCustomBlockTypes.bouncingDots }],
  });
  const initialGreetingTimeoutTimer = useRef<ReturnType<typeof setTimeout> | undefined>();
  const isInLiveChatModeFromBack = useRef(false);
  const messagesLengthRef = useRef(0);
  const streamedMessage = useRef<ChatMessageWithChangeTracker | null>(null);
  const { t } = useTranslation("common");
  const router = useRouter();
  const {
    brand: brandFromQueryParams,
    callingURL, // used to find the starter intent according to the calling URL for widget integration, this is a static context
    chatbotStaticCtxMapKey, // used to find the starter intent according to the chatbot context for widget integration, this is a static context
    dochatConfigId,
    digitalDynamicCtx, // used to find the starter intent according to the dynamic context for widget integration, this is a dynamic context
    hasNoHeader,
    isTestingMode,
    localeFromQueryParams,
    pinCode, // used to find the starter intent according to the context retrieved from contrack, it's for SMS to Bot integration, this is a dynamic context
    positionId, // used to find the starter intent according to the context associated with a voice position, it's for SMS to Bot integration, this is a static context
    sessionId,
    theming,
  } = useMemo(() => getQueryParams(router), [router]);

  const wishedLocale = useRef(localeFromQueryParams);
  useEffect(() => {
    messagesLengthRef.current = messages.filter(
      (message) => message.id && !["dotsLoading", "streamed-msg"].includes(message.id)
    ).length;
  }, [messages]);

  const resetState = useCallback(() => {
    setChatbotParams(null);
    setMessages([]);
    streamedMessage.current = null;
    didReceiveResponseToInitialGreeting.current = false;
    initialGreetingTimeoutTimer.current = undefined;
    setIsStreamingDisabled(false);
    setDisabled(true);
  }, []);

  const doBotPayloadRef = useRef<ForDoBotPayload>({
    message: "",
    metadata: {
      context: {
        pinCode: null,
        static_data: null,
      },
    },
    sender: "",
  });

  const doBotUrlRef = useRef<string | null>(null);
  const streamedMessagesReceiver = useRef<StreamedMessagesReceiver>();

  useTheming({ chatbotParams, theming });

  const isOnlyLastMessageDisplayed = useMemo(
    () => chatbotParams?.onlyLastMessage && !isInLiveChatModeFromBack.current,
    [chatbotParams?.onlyLastMessage]
  );

  const showLoadingDots = useCallback(() => {
    setMessages((messages) => [...messages, dotsLoading.current]);
  }, []);
  const hideLoadingDots = useCallback(() => {
    setMessages((messages) => [...messages.filter((msg) => msg !== dotsLoading.current)]);
  }, []);
  const refreshMessages = useCallback(() => {
    setMessages((messages) => [...messages]);
  }, []);

  const displayBlockWithFakeStreamingEffect = useCallback(
    async (block: BlockText) => {
      const textArray = block.text;
      block.text = "";
      for (let i = 0; i < textArray.length; i += FAKE_STREAMING_NB_STREAMED_LETTERS_AT_ONCE) {
        const substr = textArray.slice(i, i + FAKE_STREAMING_NB_STREAMED_LETTERS_AT_ONCE);
        block.text += substr;
        refreshMessages();
        await sleep(FAKE_STREAMING_DELAY_MS);
      }
    },
    [refreshMessages]
  );

  const displayMessageWithFakeStreamingEffect = useCallback(
    async (message: ChatMessage) => {
      // Show a message without its blocks
      const fakeStreamedMessage = { ...message };
      const streamedBlocks = fakeStreamedMessage.blocks;
      fakeStreamedMessage.blocks = [];
      // Display the message before the loading dots
      if (isOnlyLastMessageDisplayed) {
        setMessages(() => [fakeStreamedMessage, dotsLoading.current]);
      } else
        setMessages((messages) => [
          ...messages.filter((msg) => msg !== dotsLoading.current),
          fakeStreamedMessage,
          dotsLoading.current,
        ]);
      // Show the blocks one by one
      if (streamedBlocks) {
        for (const block of streamedBlocks ?? []) {
          fakeStreamedMessage.blocks.push(block);
          if (block.type === ChatCustomBlockTypes.text) {
            await displayBlockWithFakeStreamingEffect(block);
          }
          refreshMessages();
        }
      }
    },
    [displayBlockWithFakeStreamingEffect, refreshMessages, isOnlyLastMessageDisplayed]
  );

  const displayMessagesWithFakeStreamingEffect = useCallback(
    async (newMessages: ChatMessage[]) => {
      for (const message of newMessages) {
        await displayMessageWithFakeStreamingEffect(message);
      }
    },
    [displayMessageWithFakeStreamingEffect]
  );

  const clearInitialGreetingTimeout = useCallback(() => {
    if (!didReceiveResponseToInitialGreeting.current) {
      didReceiveResponseToInitialGreeting.current = true;
      clearTimeout(initialGreetingTimeoutTimer.current);
      initialGreetingTimeoutTimer.current = undefined;
    }
  }, []);

  const handleSendNewMessagesToCrai = useCallback(
    (messagesToCrai: Record<string, unknown>[]) => {
      return isTestingMode
        ? window.parent.postMessage(
            {
              action: DoChatAction.newSourcesToAdd,
              newMessages: messagesToCrai,
            },
            "*"
          )
        : null;
    },
    [isTestingMode]
  );

  const { refetch: refetchOutOfCredits } = trpc.dochatConfig.retrieveOutOfCreditsParam.useQuery(
    { id: dochatConfigId },
    {
      onSuccess: (outOfCreditsRes: boolean) => {
        setIsOutOfCredits(outOfCreditsRes);
        if (outOfCreditsRes) {
          setMessages((prev) => [...prev, getOutOfCreditsErrorMessage(t)]);
        }
      },
      refetchOnWindowFocus: false,
      retry: 3,
      enabled: false,
      refetchOnMount: true,
    }
  );

  const displayDoBotResponse = useCallback(
    async (finalPayload: DoBotResponse, isInLiveChatMode?: boolean) => {
      void refetchOutOfCredits();

      isInLiveChatModeFromBack.current = isInLiveChatMode ?? false;
      const arrivingMessages: ChatMessage[] = finalPayload.map((doBotBlock: FromDoBotBlock) => {
        messagesLengthRef.current += 1;
        const messageId = `message-${messagesLengthRef.current}`;
        return {
          blocks: doBotBlock.custom?.blocks?.map((block, blockIndex) => ({
            ...block,
            id: `${messageId}-block-${blockIndex}`,
          })),
          id: messageId,
          events: doBotBlock.custom?.events || {},
          metadata: doBotBlock.custom?.metadata,
        };
      });

      if (isTestingMode) {
        handleSendNewMessagesToCrai(
          arrivingMessages.map((message) => {
            return {
              messageId: message.id,
              userQuestion: doBotPayloadRef.current.message ?? "",
              metadata: (message.metadata?.[0] as Record<string, unknown>)?.generative_response,
            };
          })
        );
      }

      if (streamedMessage.current) {
        // Overwrite the streamedMessage with this new final message
        setMessages((messages) => {
          const newMessages = [
            ...messages.filter((msg) => msg !== streamedMessage.current),
            ...arrivingMessages,
          ];
          streamedMessage.current = null;
          return newMessages;
        });
      } else {
        // No streamed message: display received messages,
        // if option is active, do it with a streamed effect
        if (chatbotParams?.simulateStreaming) {
          await displayMessagesWithFakeStreamingEffect(arrivingMessages);
        } else {
          // Hack for spidy '\\n': Replace them with '\n'
          arrivingMessages
            .filter((msg) => msg?.blocks)
            .forEach((msg) =>
              msg.blocks
                ?.filter((block) => block.type === ChatCustomBlockTypes.text)
                .forEach((block) => {
                  block.text = block.text.replaceAll("\\n", "\n");
                })
            );
          if (isOnlyLastMessageDisplayed) {
            setMessages([...arrivingMessages]);
          } else {
            setMessages((messages) => [...messages, ...arrivingMessages]);
          }
        }
      }
      setDisabled(false);
      hideLoadingDots();
    },
    [
      chatbotParams?.simulateStreaming,
      displayMessagesWithFakeStreamingEffect,
      handleSendNewMessagesToCrai,
      hideLoadingDots,
      isOnlyLastMessageDisplayed,
      isTestingMode,
      refetchOutOfCredits,
    ]
  );

  const listenForIncomingMessages = useCallback(() => {
    if (isStreamingDisabled) {
      return;
    }
    streamedMessagesReceiver.current?.close();
    streamedMessagesReceiver.current = new StreamedMessagesReceiver(
      doBotPayloadRef.current.sender,
      {
        receivedAnswerChunk: (messageChunk: string) => {
          clearInitialGreetingTimeout();

          const oldBlock = (streamedMessage.current?.blocks ?? [])[0] as BlockText | undefined;
          const oldText = oldBlock?.text ?? "";

          const newBlock: ChatCustomBlock = {
            type: ChatCustomBlockTypes.text,
            text: oldText + messageChunk,
          };

          if (streamedMessage.current == null) {
            streamedMessage.current = {
              blocks: [newBlock],
              user: false,
              metadata: undefined,
              id: "streamed-msg",
              streamingChangesCounter: 0,
            };
            if (isOnlyLastMessageDisplayed) {
              setMessages([streamedMessage.current, dotsLoading.current]);
            } else
              setMessages(
                (messages) => [
                  ...messages.filter((m) => m !== dotsLoading.current),
                  streamedMessage.current as ChatMessage,
                  dotsLoading.current,
                ] // keep dots as last
              );
          } else {
            if (streamedMessage.current?.blocks) {
              streamedMessage.current.blocks[0] = newBlock;
              // Perform a shallow change on message, allowing ChatMessageUi to refresh
              streamedMessage.current.streamingChangesCounter =
                (streamedMessage.current.streamingChangesCounter ?? 0) + 1;
            }
            refreshMessages();
          }
        },

        receivedFinalMessage: (finalPayload: DoBotResponse, isInLiveChatMode?: boolean) => {
          clearInitialGreetingTimeout();
          void displayDoBotResponse(finalPayload, isInLiveChatMode);
        },

        onError: () => {
          streamedMessage.current = null;
          hideLoadingDots();
          if (!didReceiveResponseToInitialGreeting.current) {
            // will relaunch askDoBot without SSE
            setIsStreamingDisabled(true);
          } else {
            // Append the error message
            setMessages((messages) => [...messages, getGenericErrorMessage(t)]);
            setDisabled(false);
          }
        },
      }
    );
  }, [
    clearInitialGreetingTimeout,
    displayDoBotResponse,
    hideLoadingDots,
    isStreamingDisabled,
    refreshMessages,
    isOnlyLastMessageDisplayed,
    t,
  ]);

  // Allow to focus the input to type a text to the chatbot when it become enabled (!disabled)
  useEffect(() => {
    if (!disabled) {
      chatInputRef?.current?.focus();
    }
  }, [disabled]);

  const startInitialGreetingTimeout = useCallback(async () => {
    initialGreetingTimeoutTimer.current = setTimeout(() => {
      // eslint-disable-next-line no-console
      console.error("SSE fell into timeout");
      streamedMessagesReceiver.current?.close();
      streamedMessagesReceiver.current = undefined;

      setMessages((messages) => {
        const newMessages = [...messages.filter((msg) => msg !== streamedMessage.current)];
        streamedMessage.current = null;
        return newMessages;
      });

      hideLoadingDots();
      // changing isStreamingDisabled will relaunch askDoBot
      setIsStreamingDisabled(true);
    }, TIMEOUT_AFTER_GREETING_BEFORE_DISABLING_SSE_MS);
  }, [hideLoadingDots]);

  const askDoBot = useCallback(async () => {
    try {
      if (!doBotUrlRef.current) throw new Error("Trying to use askDoBot without a do-bot URL.");
      if (!isInLiveChatModeFromBack.current) {
        showLoadingDots();
        setDisabled(true);
      } else {
        setDisabled(false);
      }
      if (isStreamingDisabled) {
        const response = await sendQuestionToDoBot(doBotUrlRef.current, doBotPayloadRef.current);
        await displayDoBotResponse(response);
      } else {
        await sendQuestionToDoChat(doBotUrlRef.current, doBotPayloadRef.current);
        if (!didReceiveResponseToInitialGreeting.current) {
          await startInitialGreetingTimeout();
        }
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error("Error while posting question", error);
      setMessages((messages) => [...messages, getMessageForError(t, error)]);
      setDisabled(false);
      hideLoadingDots();
    }
  }, [
    displayDoBotResponse,
    hideLoadingDots,
    isStreamingDisabled,
    showLoadingDots,
    startInitialGreetingTimeout,
    t,
  ]);

  const { refetch } = trpc.dochatConfig.retrieveParams.useQuery(
    {
      brandFromQParams: brandFromQueryParams || null,
      callingURL: callingURL || null,
      chatbotStaticCtxMapKey: chatbotStaticCtxMapKey || null,
      digitalDynamicCtx: digitalDynamicCtx || null, // we send it server side to create a pincode and store it in contrack
      id: dochatConfigId,
      pinCode: pinCode || null, // we send it server side to retrieve the context associated with the pincode
      positionId: positionId || null,
      sessionId: sessionId || null,
      theming: theming || null,
      userLang: wishedLocale.current || null,
    },
    {
      onSuccess: ({
        analyticId,
        chatbotParams,
        company_cuid,
        computedAvailableLocales,
        computedBrand,
        computedLanguage,
        computedStartMessage,
        computedStaticData,
        createdPinCode,
        mergedCtxData,
        orchestrator_id,
        outOfCredits,
        rasaUrl: doBotUrl,
      }) => {
        setIsOutOfCredits(outOfCredits);
        if (outOfCredits) setMessages((prev) => [...prev, getOutOfCreditsErrorMessage(t)]);
        setMergedCtxData(mergedCtxData);
        doBotPayloadRef.current.message = computedStartMessage;
        doBotPayloadRef.current.sender = analyticId;
        doBotPayloadRef.current.metadata = {
          ...doBotPayloadRef.current.metadata,
          brand: computedBrand,
          clickedSolData: "",
          company_cuid,
          orchestrator_id,
          testing: isTestingMode,
          context: {
            pinCode: pinCode || createdPinCode || null,
            static_data: computedStaticData,
          },
        };
        doBotPayloadRef.current.dochatConfigId = dochatConfigId;
        doBotUrlRef.current = doBotUrl;
        // This orchestrator is internationalized
        const availableLocales = computedAvailableLocales || chatbotParams.availableLocales;
        if (availableLocales) {
          chatbotParams.availableLocales = availableLocales;
          localeSwitcher({
            availableLocales: availableLocales,
            defaultModelLanguage: chatbotParams.defaultModelLanguage,
            doBotPayloadRef,
            doBotUrlRef,
            requestedLocale: computedLanguage,
            setCurrentLocale,
          });
        }
        setChatbotParams(chatbotParams);
      },
      refetchOnWindowFocus: false,
      retry: 3,
      enabled: false,
      refetchOnMount: true,
    }
  );

  // Display lang in HTML's root
  useEffect(() => {
    if (!chatbotParams) return void refetch();
    const html = document.querySelector<HTMLElement>("html");
    if (html) html.lang = currentLocale?.split("_")[0] || "fr";
  }, [chatbotParams, currentLocale, refetch]);

  useEffect(() => {
    if (chatbotParams && !isOutOfCredits && doBotUrlRef.current) {
      listenForIncomingMessages();
      void askDoBot();
    }
  }, [chatbotParams, isOutOfCredits, doBotUrlRef, askDoBot, listenForIncomingMessages]);

  const handleSubmit = useCallback(
    async (userMessage: string | ChatFastAction) => {
      doBotPayloadRef.current.metadata = {
        ...doBotPayloadRef.current.metadata,
        form: undefined,
        isStreamingEnabled: !isStreamingDisabled,
        context: {},
      };
      if (typeof userMessage === "string") {
        doBotPayloadRef.current.message = userMessage;
      } else {
        doBotPayloadRef.current.message = userMessage.payload || "/trigger_rating_feedback";
        if (userMessage.payload && userMessage.metadata) {
          doBotPayloadRef.current.metadata = {
            ...doBotPayloadRef.current.metadata,
            ...userMessage.metadata[0],
          };
        }
        if (userMessage.code || userMessage.tel || userMessage.url) {
          doBotPayloadRef.current.metadata = {
            ...doBotPayloadRef.current.metadata,
            clickedSolData: userMessage,
          };
        }
      }

      const messageText =
        typeof userMessage === "string"
          ? userMessage
          : userMessage.tel
          ? `📞 ${userMessage.tel}.`
          : userMessage.title;
      if (messageText) {
        setMessages((previousMessages) => {
          messagesLengthRef.current += 1;
          const ret: ChatMessage[] = [
            ...previousMessages,
            {
              blocks: [
                {
                  type: ChatCustomBlockTypes.text,
                  text: messageText,
                  id: `message-${messagesLengthRef.current}-block-0`,
                },
              ],
              user: true,
              id: `message-${messagesLengthRef.current}`,
            },
          ];
          return ret;
        });
      }

      await askDoBot();
      doBotPayloadRef.current.metadata.clickedSolData = "";
    },
    [askDoBot, isStreamingDisabled]
  );

  const handleChangeLocale = useCallback(
    async (selectedLocale: Locale) => {
      if (chatbotParams?.availableLocales && doBotUrlRef.current) {
        await handleSubmit("/changeLocale");
        wishedLocale.current = selectedLocale;
        resetState();
      }
    },
    [chatbotParams?.availableLocales, handleSubmit, resetState]
  );

  const handleFastAction: ChatContainerUiProps["onFastAction"] = useCallback(
    (resp) => {
      // When fsmDef, there's no action sent to do-bot
      if (resp.fsmDef) {
        setMessages((messages: ChatMessage[]) => {
          const lastMessage = messages.at(-1) as ChatMessage;
          const lastBlocks = lastMessage?.blocks || [];
          return [
            ...messages.slice(0, -1),
            {
              ...lastMessage,
              blocks: [
                ...lastBlocks,
                {
                  type: ChatCustomBlockTypes.steps,
                  fsmDef: resp.fsmDef,
                  title: "",
                },
              ],
            },
          ] as ChatMessage[];
        });
        return;
        // For other types, we handle it specifically and always send the action to do-bot
      } else if (resp.code) {
        const script = document.createElement("script");
        const scriptContent = document.createTextNode(resp.code);
        script.appendChild(scriptContent);
        document.body.appendChild(script);
      } else if (resp.tel) {
        window.open(resp.tel.match(/^tel/) ? resp.tel : `tel:${resp.tel}`);
      } else if (resp.url) {
        window.open(appendHttpsSchemeIfNecessary(resp.url));
      }
      void handleSubmit(resp);
    },
    [handleSubmit]
  );

  const handleHandoverRequest = useCallback(() => {
    void handleSubmit(
      `/get_response{"button_response_id":"${chatbotParams?.handover?.handoverStartCuid}"}`
    );
  }, [chatbotParams?.handover?.handoverStartCuid, handleSubmit]);

  const handleEditBotResponse: EditBotResponseProps["onEditBotResponse"] = useCallback(
    (messageId: string, openDialog = false) => {
      return isTestingMode
        ? window.parent.postMessage(
            { action: DoChatAction.editBotResponse, messageId, openDialog },
            "*"
          )
        : null;
    },
    [isTestingMode]
  );

  const handleRefreshConversation = useCallback(() => {
    if (isTestingMode) window.parent.postMessage({ action: DoChatAction.resetUsedSources }, "*");
    resetState();
  }, [isTestingMode, resetState]);

  const title = useMemo(
    () =>
      currentLocale &&
      chatbotParams?.availableLocales &&
      chatbotParams.availableLocales[currentLocale]?.title
        ? chatbotParams.availableLocales[currentLocale].title
        : chatbotParams?.title || "do-chat",
    [currentLocale, chatbotParams]
  );
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta property="og:title" content={title} />
        {chatbotParams && <link rel="icon" href={chatbotParams.logoDataUri} />}
      </Head>
      <Box sx={staticRootStyles}>
        {!chatbotParams ? (
          <Box alignItems="center" display="flex" justifyContent="center" minHeight="100vh">
            <CircularProgress />
          </Box>
        ) : (
          <ChatContainerUi
            chatbotParams={chatbotParams}
            currentLocale={currentLocale}
            disabled={disabled || isOutOfCredits}
            inputRef={chatInputRef}
            isOutOfCredits={isOutOfCredits}
            isTestingMode={isTestingMode}
            mergedCtxData={mergedCtxData}
            messages={messages}
            noHeader={hasNoHeader}
            onChangeLocale={handleChangeLocale}
            onEditBotResponse={handleEditBotResponse}
            onFastAction={handleFastAction}
            onHandoverRequest={handleHandoverRequest}
            onRefreshConversation={handleRefreshConversation}
            onSubmit={handleSubmit}
            readonlyModeMessage={isOutOfCredits ? t("disabledChatbotError") : undefined}
          />
        )}
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  query,
}: GetServerSidePropsContext) => {
  const locale = (query.locale as string) ? (query.locale as string).split("_")[0] : "fr";
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
};

export default DochatConfigPage;
